import {_} from 'lodash';
import {ImportFormatId} from '../../../constants/ImportFormats';
import {ObjectTypeId} from '../../../constants';
import {IRule, TSyncfield} from '../../../index';
import {global} from '../../../appContext';
import {getObjectDefinition, getRawSyncFieldValue} from '../../../util/misc';
import {nsRuleset} from '../../ruleset/nsRuleset';
import {nsSyncFields} from '../../../req/nsSyncFields';
import generateRule = nsRuleset.generateRule;
import TObjectData_basic = nsSyncFields.TObjectData_basic;

const DEPARTMENT_SYNCFIELD_NAMES = (getObjectDefinition(ObjectTypeId.Department)?.syncfieldDisplayName ?? []) as TSyncfield[];

/** Syncfields loaded for each member row (search enrichment and filter tabs). */
export const MEMBER_ROW_SYNCFIELD_NAMES: readonly TSyncfield[] = [
    'GivenName',
    'Surname',
    'EmployeeNumber',
    'DepartmentNumber',
    'Manager',
    'AuthDataSource',
    'SourceId',
];

/**
 * Syncfields requested via `searchobject` `onlytuples` when enriching current-tab members.
 * `SourceId` is required for `createoverride` (`targetobjectsrc`).
 */
export const MEMBER_CURRENT_TAB_USER_TUPLE_NAMES: readonly TSyncfield[] = [
    'Id',
    'DepartmentNumber',
    'Department',
    'Manager',
    'AuthDataSource',
    'SourceId',
];

/** Collects distinct department org-unit numbers from member rows. */
export function collectDepartmentOrgUnitNumbers(users: TObjectData_basic[]): string[] {
    return _.chain(users)
        .map((u: TObjectData_basic) => getRawSyncFieldValue('DepartmentNumber', u.syncfields))
        .filter((n: string | null | undefined): n is string => !!n && n !== '0' && n !== 'Root')
        .uniq()
        .value();
}

/** Collects distinct manager reference values from member rows. */
export function collectManagerReferenceValues(users: TObjectData_basic[]): string[] {
    return _.chain(users)
        .map((u: TObjectData_basic) => getRawSyncFieldValue('Manager', u.syncfields))
        .filter((m: string | null | undefined): m is string => !!m?.trim())
        .uniq()
        .value();
}

/**
 * Groups distinct `Manager` values by member `AuthDataSource` (same id as import format).
 * Mirrors per-field manager links in `nsSyncFields` / `getRulesForFetchingAManager`.
 */
export function collectManagerReferencesByImportFormat(
    users: TObjectData_basic[],
): Map<ImportFormatId, string[]> {
    const orgFormatId = global.login.selectedOrganization()?.importformat?.id;
    const grouped = new Map<ImportFormatId, Set<string>>();

    for (const user of users) {
        const managerRef = getRawSyncFieldValue('Manager', user.syncfields)?.trim();
        if (!managerRef) {
            continue;
        }
        const authDataSource = getRawSyncFieldValue('AuthDataSource', user.syncfields);
        const formatId = (authDataSource ? Number(authDataSource) : orgFormatId) as ImportFormatId;
        if (formatId == null || Number.isNaN(formatId)) {
            continue;
        }
        let refs = grouped.get(formatId);
        if (!refs) {
            refs = new Set();
            grouped.set(formatId, refs);
        }
        refs.add(managerRef);
    }

    const result = new Map<ImportFormatId, string[]>();
    grouped.forEach((refs, formatId) => result.set(formatId, Array.from(refs)));
    return result;
}

/** Ruleset rule matching departments by org-unit number (batch). */
export function buildDepartmentBatchRule(orgUnitNumbers: string[]): IRule | null {
    if (!orgUnitNumbers.length) {
        return null;
    }
    return generateRule({
        attribute: 'OrgUnitnr',
        argumentoperator: 'Er lik en av',
        argument: orgUnitNumbers.join(','),
    });
}

/**
 * Ruleset rules matching managers for an import format / AuthDataSource (batch).
 * Mirrors `getRulesForFetchingAManager` but uses "Er lik en av" for all branches.
 */
export function buildManagerBatchRules(
    managerValues: string[],
    importFormatId: ImportFormatId = global.login.selectedOrganization()?.importformat?.id ?? ImportFormatId.Visma,
): IRule[] {
    if (!managerValues.length) {
        return [];
    }
    const argument = managerValues.join(',');

    switch (importFormatId) {
        case ImportFormatId.Visma:
        case ImportFormatId.Visma_Hierachy:
            return [
                generateRule({
                    attribute: 'UserId',
                    argumentoperator: 'Er lik en av',
                    argument,
                    booleanoperator: 'Or',
                    order: 1,
                }),
                generateRule({
                    attribute: 'AUserId',
                    argumentoperator: 'Er lik en av',
                    argument,
                    booleanoperator: 'Or',
                    order: 2,
                }),
            ];
        case ImportFormatId.Personec_P_HR:
        case ImportFormatId.Personec_P_HI:
        case ImportFormatId.Visma_Employee_HR:
        case ImportFormatId.Visma_Employee_HI:
            return [generateRule({
                attribute: 'SourceId',
                argumentoperator: 'Er lik en av',
                argument,
            })];
        default:
            return [
                generateRule({
                    attribute: 'AEmployeeNumber',
                    argumentoperator: 'Er lik en av',
                    argument,
                    order: 1,
                }),
                generateRule({
                    attribute: 'EmployeeNumber',
                    argumentoperator: 'Er lik en av',
                    argument,
                    booleanoperator: 'Or',
                    order: 2,
                }),
            ];
    }
}

/** Maps org-unit number to a department object from a tuple search. */
export function buildDepartmentLookupMap(departments: TObjectData_basic[]): ReadonlyMap<string, TObjectData_basic> {
    const map = new Map<string, TObjectData_basic>();
    for (const department of departments) {
        const orgUnitnr = getRawSyncFieldValue('OrgUnitnr', department.syncfields);
        if (orgUnitnr) {
            map.set(orgUnitnr, department);
        }
    }
    return map;
}

/** Maps manager reference values (UserId, SourceId, etc.) to user objects from a tuple search. */
export function buildManagerLookupMap(managers: TObjectData_basic[]): ReadonlyMap<string, TObjectData_basic> {
    const map = new Map<string, TObjectData_basic>();
    const indexKeys: TSyncfield[] = ['UserId', 'AUserId', 'SourceId', 'EmployeeNumber', 'AEmployeeNumber'];
    for (const manager of managers) {
        for (const key of indexKeys) {
            const value = getRawSyncFieldValue(key, manager.syncfields);
            if (value && !map.has(value)) {
                map.set(value, manager);
            }
        }
    }
    return map;
}

export function getDepartmentLookupSyncfieldNames(): TSyncfield[] {
    return _.uniq<TSyncfield>(['Id', 'OrgUnitnr', ...DEPARTMENT_SYNCFIELD_NAMES]);
}

const MANAGER_INDEX_SYNCFIELDS = ['UserId', 'AUserId', 'AEmployeeNumber'] as const satisfies readonly TSyncfield[];

export function getManagerLookupSyncfieldNames(): TSyncfield[] {
    return _.uniq<TSyncfield>([
        'Id',
        ...(getObjectDefinition(ObjectTypeId.User)?.syncfieldDisplayName ?? []) as TSyncfield[],
        ...MANAGER_INDEX_SYNCFIELDS,
    ]);
}
